import React, { useState } from "react";
import styled from "@emotion/styled";
import BillingConfigEditor from "./BillingConfigEditor";
import RevenueTable from "./RevenueTable";
import RevenueContainer from "../../../state/containers/RevenueContainer";
import {
    BillingConfig,
    OrganizationConfig,
    OrganizationRevenue,
    DEFAULT_BILLING_CONFIG,
    ContractType,
    CONTRACT_TYPES,
    ContractStatus,
    CONTRACT_STATUSES,
} from "../../../models/revenue/BillingConfig";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    color_shades_darker,
} from "../../../constants/colors";
import Loader from "../../../components/general/Loader";

interface OrganizationSectionProps {
    orgConfig: OrganizationConfig;
    orgRevenue: OrganizationRevenue | undefined;
    revenueContainer: RevenueContainer;
    isExpanded: boolean;
    isLoading: boolean;
    onToggleExpand: () => void;
    onRemove: () => void;
    clipboardConfig: BillingConfig | null;
    onCopyConfig: (config: BillingConfig) => void;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const getBillingSummary = (config: BillingConfig): string => {
    const items: string[] = [];
    
    if (config.aiMinuteRate > 0) {
        items.push(`AI: ${formatCurrency(config.aiMinuteRate)}/min`);
    }
    if (config.postTransferMinuteRate > 0) {
        items.push(`PT: ${formatCurrency(config.postTransferMinuteRate)}/min`);
    }
    if (config.callRate > 0) {
        items.push(`Calls: ${formatCurrency(config.callRate)}`);
    }
    if (config.containedCallRate > 0) {
        items.push(`Contained: ${formatCurrency(config.containedCallRate)}`);
    }
    if (config.nonContainableCallRate > 0) {
        items.push(`Non-containable: ${formatCurrency(config.nonContainableCallRate)}`);
    }
    if (config.orderPlacedRate > 0) {
        items.push(`Orders: ${formatCurrency(config.orderPlacedRate)}`);
    }
    if (config.flatMonthlyFee > 0) {
        items.push(`Flat: ${formatCurrency(config.flatMonthlyFee)}/mo`);
    }
    
    return items.length > 0 ? items.join(" • ") : "No billing configured";
};

const OrganizationSection: React.FC<OrganizationSectionProps> = ({
    orgConfig,
    orgRevenue,
    revenueContainer,
    isExpanded,
    isLoading,
    onToggleExpand,
    onRemove,
    clipboardConfig,
    onCopyConfig,
}) => {
    const [showConfig, setShowConfig] = useState(false);
    const [editingOverrideAppId, setEditingOverrideAppId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);

    const orgId = orgConfig.organizationId;
    const engagementCount = (revenueContainer.state.engagementsByOrg[orgId] || []).length;
    const appCount = (revenueContainer.state.applicationsByOrg[orgId] || []).filter(app =>
        app.isLive !== false &&
        !orgConfig.ignoredApplicationIds.includes(app.id)
    ).length;
    
    // Get all apps for this org
    const allApps = revenueContainer.state.applicationsByOrg[orgId] || [];
    
    // Filter apps based on search and exclude already force-included ones
    const forceIncludedIds = orgConfig.forceIncludeApplicationIds || [];
    const availableApps = searchTerm.trim().length > 0 
        ? allApps.filter(app => 
            !forceIncludedIds.includes(app.id) &&
            (app.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
             app.id.toLowerCase().includes(searchTerm.toLowerCase()))
          )
        : [];

    const handleEditOverride = (appId: string) => {
        setEditingOverrideAppId(editingOverrideAppId === appId ? null : appId);
    };

    const getOverrideConfig = (appId: string): BillingConfig | null => {
        const override = orgConfig.applicationOverrides.find(
            (ao) => ao.applicationId === appId
        );
        return override ? override.config : null;
    };

    const handleOverrideChange = (appId: string, config: BillingConfig) => {
        revenueContainer.addApplicationOverride(orgId, appId, config);
    };

    const handleRemoveOverride = (appId: string) => {
        revenueContainer.removeApplicationOverride(orgId, appId);
        setEditingOverrideAppId(null);
    };

    const handleExportOrgConfig = () => {
        try {
            const jsonString = revenueContainer.exportOrgConfig(orgId);
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `org-${orgConfig.organizationName || orgId}-config.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(`Failed to export config: ${e.message}`);
        }
    };

    const handleImportOrgConfig = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json";
        input.onchange = (e: any) => {
            const file = e.target?.files?.[0];
            if (!file) return;
            
            const reader = new FileReader();
            reader.onload = (event) => {
                const jsonString = event.target?.result as string;
                const result = revenueContainer.importOrgConfig(jsonString);
                if (result.success) {
                    alert("Organization config imported successfully!");
                } else {
                    alert(`Failed to import config: ${result.error}`);
                }
            };
            reader.readAsText(file);
        };
        input.click();
    };

    const handleExportAppConfig = (appId: string) => {
        try {
            const jsonString = revenueContainer.exportAppConfig(orgId, appId);
            const app = orgRevenue?.applications.find(a => a.applicationId === appId);
            const appName = app?.applicationName || appId;
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `app-${appName}-config.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(`Failed to export config: ${e.message}`);
        }
    };

    const handleImportAppConfig = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json";
        input.onchange = (e: any) => {
            const file = e.target?.files?.[0];
            if (!file) return;
            
            const reader = new FileReader();
            reader.onload = (event) => {
                const jsonString = event.target?.result as string;
                const result = revenueContainer.importAppConfig(orgId, jsonString);
                if (result.success) {
                    alert("Application config imported successfully!");
                } else {
                    alert(`Failed to import config: ${result.error}`);
                }
            };
            reader.readAsText(file);
        };
        input.click();
    };

    return (
        <Wrapper>
            <OrgHeader onClick={onToggleExpand}>
                <HeaderLeft>
                    <ExpandIcon>{isExpanded ? "\u25BC" : "\u25B6"}</ExpandIcon>
                    <OrgName>{orgConfig.organizationName || orgConfig.organizationId}</OrgName>
                    <ContractTypeSelect
                        value={orgConfig.contractType || ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            e.stopPropagation();
                            const val = e.target.value as ContractType | "";
                            revenueContainer.updateOrgContractType(
                                orgId,
                                val === "" ? undefined : val
                            );
                        }}
                        title="Contract type"
                    >
                        <option value="">No contract type</option>
                        {CONTRACT_TYPES.map((ct) => (
                            <option key={ct} value={ct}>{ct}</option>
                        ))}
                    </ContractTypeSelect>
                    {isLoading && <LoadingBadge>Loading...</LoadingBadge>}
                    {!isLoading && orgRevenue && (
                        <RevenueBadge>{formatCurrency(orgRevenue.totalRevenue)}</RevenueBadge>
                    )}
                    {orgConfig.contractStatus && (
                        <ContractStatusBadge status={orgConfig.contractStatus}>
                            {orgConfig.contractStatus}
                        </ContractStatusBadge>
                    )}
                    {orgConfig.totalPossibleLocations != null && (
                        <TotalLocationsBadge title="Total possible locations">
                            {orgConfig.totalPossibleLocations} locations
                        </TotalLocationsBadge>
                    )}
                    <BillingSummary title="Default billing configuration">
                        {getBillingSummary(orgConfig.defaultConfig)}
                    </BillingSummary>
                </HeaderLeft>
                <HeaderRight>
                    {clipboardConfig && (
                        <PasteConfigButton
                            onClick={(e) => {
                                e.stopPropagation();
                                revenueContainer.updateOrgDefaultConfig(orgId, clipboardConfig);
                            }}
                            title="Paste billing config from clipboard"
                        >
                            Paste Config
                        </PasteConfigButton>
                    )}
                    <StatBadge>{appCount} locations</StatBadge>
                    <StatBadge>{engagementCount.toLocaleString()} conversations</StatBadge>
                    <RemoveOrgButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        title="Remove organization"
                    >
                        X
                    </RemoveOrgButton>
                </HeaderRight>
            </OrgHeader>

            {isExpanded && (
                <OrgBody>
                    <ConfigToggle onClick={() => setShowConfig(!showConfig)}>
                        {showConfig ? "Hide Billing Config" : "Show Billing Config"}
                    </ConfigToggle>

                    {showConfig && (
                        <ConfigSection>
                            <OrgMetadataRow>
                                <MetadataField>
                                    <MetadataLabel>Contract Status</MetadataLabel>
                                    <MetadataSelect
                                        value={orgConfig.contractStatus || ""}
                                        onChange={(e) => {
                                            const val = e.target.value as ContractStatus | "";
                                            revenueContainer.updateOrgContractStatus(
                                                orgId,
                                                val === "" ? undefined : val
                                            );
                                        }}
                                    >
                                        <option value="">Not set</option>
                                        {CONTRACT_STATUSES.map((cs) => (
                                            <option key={cs} value={cs}>{cs}</option>
                                        ))}
                                    </MetadataSelect>
                                </MetadataField>
                                <MetadataField>
                                    <MetadataLabel>Total Possible Locations</MetadataLabel>
                                    <MetadataInput
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="Not set"
                                        value={orgConfig.totalPossibleLocations ?? ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            revenueContainer.updateOrgTotalPossibleLocations(
                                                orgId,
                                                val === "" ? undefined : Math.floor(Number(val))
                                            );
                                        }}
                                    />
                                </MetadataField>
                                <MetadataField>
                                    <MetadataLabel>Avg Revenue / Location Override</MetadataLabel>
                                    <MetadataInput
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="Computed from data"
                                        value={orgConfig.avgRevenuePerLocation ?? ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            revenueContainer.updateOrgAvgRevenuePerLocation(
                                                orgId,
                                                val === "" ? undefined : Number(val)
                                            );
                                        }}
                                    />
                                </MetadataField>
                            </OrgMetadataRow>

                            <ExportImportBar>
                                <ExportImportButton onClick={handleExportOrgConfig} title="Export this organization's full config to JSON file">
                                    Export Org Config
                                </ExportImportButton>
                                <ExportImportButton onClick={handleImportOrgConfig} title="Import organization config from JSON file">
                                    Import Org Config
                                </ExportImportButton>
                            </ExportImportBar>
                            
                            <BillingConfigEditor
                                config={orgConfig.defaultConfig}
                                onChange={(config) =>
                                    revenueContainer.updateOrgDefaultConfig(orgId, config)
                                }
                                title="Organization Default Billing Config"
                                onCopy={() => onCopyConfig(orgConfig.defaultConfig)}
                                onPaste={(config) =>
                                    revenueContainer.updateOrgDefaultConfig(orgId, config)
                                }
                                onReset={() =>
                                    revenueContainer.updateOrgDefaultConfig(
                                        orgId,
                                        { ...DEFAULT_BILLING_CONFIG }
                                    )
                                }
                                clipboardConfig={clipboardConfig}
                            />
                            
                            <ForceIncludeSection>
                                <ForceIncludeTitle>Force Include Applications (always show, even with 0 conversations)</ForceIncludeTitle>
                                <DropdownContainer>
                                    <SearchInput
                                        type="text"
                                        placeholder="Search applications..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                        onBlur={() => {
                                            // Delay to allow click on dropdown items
                                            setTimeout(() => setShowDropdown(false), 200);
                                        }}
                                    />
                                    {showDropdown && availableApps.length > 0 && (
                                        <DropdownList>
                                            {availableApps.slice(0, 10).map((app) => (
                                                <DropdownItem
                                                    key={app.id}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault(); // Prevent blur
                                                        revenueContainer.addForceIncludeApplication(orgId, app.id);
                                                        setSearchTerm("");
                                                        setShowDropdown(false);
                                                    }}
                                                >
                                                    <AppName>{app.name || "Unnamed App"}</AppName>
                                                    <AppId>{app.id}</AppId>
                                                </DropdownItem>
                                            ))}
                                            {availableApps.length > 10 && (
                                                <DropdownHint>
                                                    ...and {availableApps.length - 10} more (refine search)
                                                </DropdownHint>
                                            )}
                                        </DropdownList>
                                    )}
                                    {showDropdown && searchTerm && availableApps.length === 0 && (
                                        <DropdownList>
                                            <DropdownHint>No matching applications found</DropdownHint>
                                        </DropdownList>
                                    )}
                                </DropdownContainer>
                                {orgConfig.forceIncludeApplicationIds && orgConfig.forceIncludeApplicationIds.length > 0 && (
                                    <ForceIncludeList>
                                        {orgConfig.forceIncludeApplicationIds.map((appId) => {
                                            const app = (revenueContainer.state.applicationsByOrg[orgId] || []).find(a => a.id === appId);
                                            return (
                                                <ForceIncludeItem key={appId}>
                                                    <ForceIncludeAppName>
                                                        {app?.name || appId}
                                                        {app?.name && <ForceIncludeAppId>{appId}</ForceIncludeAppId>}
                                                    </ForceIncludeAppName>
                                                    <RemoveButton
                                                        onClick={() => revenueContainer.removeForceIncludeApplication(orgId, appId)}
                                                        title="Remove from force-include list"
                                                    >
                                                        Remove
                                                    </RemoveButton>
                                                </ForceIncludeItem>
                                            );
                                        })}
                                    </ForceIncludeList>
                                )}
                            </ForceIncludeSection>

                            <IgnoredSection>
                                <IgnoredTitle>Ignored Applications (excluded from revenue calculations)</IgnoredTitle>
                                {orgConfig.ignoredApplicationIds && orgConfig.ignoredApplicationIds.length > 0 ? (
                                    <IgnoredList>
                                        {orgConfig.ignoredApplicationIds.map((appId) => {
                                            const app = (revenueContainer.state.applicationsByOrg[orgId] || []).find(a => a.id === appId);
                                            return (
                                                <IgnoredItem key={appId}>
                                                    <IgnoredAppName>
                                                        {app?.name || appId}
                                                        {app?.name && <IgnoredAppId>{appId}</IgnoredAppId>}
                                                    </IgnoredAppName>
                                                    <RemoveButton
                                                        onClick={() => revenueContainer.toggleIgnoreApplication(orgId, appId)}
                                                        title="Remove from ignored list (include in calculations)"
                                                    >
                                                        Include
                                                    </RemoveButton>
                                                </IgnoredItem>
                                            );
                                        })}
                                    </IgnoredList>
                                ) : (
                                    <EmptyIgnoredText>No applications are currently ignored. Use the "Ignore" button in the revenue table to exclude applications.</EmptyIgnoredText>
                                )}
                            </IgnoredSection>
                        </ConfigSection>
                    )}

                    {isLoading ? (
                        <LoadingContainer>
                            <Loader />
                            <LoadingText>Loading conversation data...</LoadingText>
                        </LoadingContainer>
                    ) : orgRevenue ? (
                        <>
                            <RevenueTable
                                orgRevenue={orgRevenue}
                                ignoredAppIds={orgConfig.ignoredApplicationIds}
                                onToggleIgnore={(appId) =>
                                    revenueContainer.toggleIgnoreApplication(orgId, appId)
                                }
                                onEditOverride={handleEditOverride}
                                hasOverride={(appId) =>
                                    orgConfig.applicationOverrides.some(
                                        (ao) => ao.applicationId === appId
                                    )
                                }
                            />

                            {editingOverrideAppId && (
                                <OverrideEditor>
                                    <OverrideHeader>
                                        <OverrideTitle>
                                            Billing Override for: {
                                                orgRevenue.applications.find(
                                                    (a) => a.applicationId === editingOverrideAppId
                                                )?.applicationName || editingOverrideAppId
                                            }
                                        </OverrideTitle>
                                        <OverrideActions>
                                            {getOverrideConfig(editingOverrideAppId) && (
                                                <>
                                                    <ExportButton
                                                        onClick={() => handleExportAppConfig(editingOverrideAppId)}
                                                        title="Export this app's config to JSON file"
                                                    >
                                                        Export
                                                    </ExportButton>
                                                    <RemoveOverrideButton
                                                        onClick={() => handleRemoveOverride(editingOverrideAppId)}
                                                    >
                                                        Remove Override
                                                    </RemoveOverrideButton>
                                                </>
                                            )}
                                            {!getOverrideConfig(editingOverrideAppId) && (
                                                <ImportButton
                                                    onClick={handleImportAppConfig}
                                                    title="Import app config from JSON file"
                                                >
                                                    Import
                                                </ImportButton>
                                            )}
                                            <CloseButton onClick={() => setEditingOverrideAppId(null)}>
                                                Close
                                            </CloseButton>
                                        </OverrideActions>
                                    </OverrideHeader>
                                    <BillingConfigEditor
                                        config={
                                            getOverrideConfig(editingOverrideAppId) ||
                                            orgConfig.defaultConfig
                                        }
                                        onChange={(config) =>
                                            handleOverrideChange(editingOverrideAppId, config)
                                        }
                                        onCopy={() =>
                                            onCopyConfig(
                                                getOverrideConfig(editingOverrideAppId) ||
                                                orgConfig.defaultConfig
                                            )
                                        }
                                        onPaste={(config) =>
                                            handleOverrideChange(editingOverrideAppId, config)
                                        }
                                        clipboardConfig={clipboardConfig}
                                    />
                                </OverrideEditor>
                            )}
                        </>
                    ) : (
                        <EmptyState>
                            No data loaded. Click "Load Data" to fetch conversation data for this month.
                        </EmptyState>
                    )}
                </OrgBody>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;
`;

const OrgHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    background: ${color_shades_lightest};
    cursor: pointer;
    user-select: none;

    &:hover {
        background: ${color_shades_light};
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ExpandIcon = styled.span`
    font-size: 12px;
    color: ${color_text_light};
    width: 16px;
`;

const OrgName = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${color_text_default};
`;

const ContractTypeSelect = styled.select`
    font-size: 11px;
    color: ${color_text_light};
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 3px;
    padding: 3px 6px;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
`;

const ContractStatusBadge = styled.span<{ status: ContractStatus }>`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 3px;
    color: ${({ status }) => (status === "Won" ? color_colors_growth : color_colors_ocean)};
    background: ${({ status }) =>
        status === "Won" ? "rgba(88, 189, 153, 0.1)" : "rgba(30, 126, 185, 0.1)"};
`;

const TotalLocationsBadge = styled.span`
    font-size: 11px;
    color: ${color_text_light};
    background: ${color_defaults_white};
    padding: 3px 8px;
    border-radius: 3px;
    border: 1px solid ${color_shades_light};
`;

const OrgMetadataRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 12px;
    padding: 10px 12px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
`;

const MetadataField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const MetadataLabel = styled.label`
    font-size: 11px;
    font-weight: 600;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const MetadataSelect = styled.select`
    font-size: 13px;
    padding: 4px 8px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: ${color_text_default};

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
`;

const MetadataInput = styled.input`
    font-size: 13px;
    padding: 4px 8px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    width: 120px;
    color: ${color_text_default};

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
`;

const RevenueBadge = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${color_colors_growth};
    background: rgba(88, 189, 153, 0.1);
    padding: 3px 10px;
    border-radius: 4px;
`;

const LoadingBadge = styled.span`
    font-size: 12px;
    color: ${color_colors_ocean};
    font-style: italic;
`;

const StatBadge = styled.span`
    font-size: 12px;
    color: ${color_text_light};
    background: ${color_defaults_white};
    padding: 3px 8px;
    border-radius: 3px;
    border: 1px solid ${color_shades_light};
`;

const BillingSummary = styled.span`
    font-size: 11px;
    color: ${color_text_light};
    background: ${color_shades_lightest};
    padding: 4px 8px;
    border-radius: 3px;
    border: 1px solid ${color_shades_light};
    font-family: monospace;
    max-width: 500px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RemoveOrgButton = styled.button`
    background: transparent;
    color: ${color_colors_decline};
    border: 1px solid ${color_colors_decline};
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: ${color_colors_decline};
        color: ${color_defaults_white};
    }
`;

const PasteConfigButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    font-size: 12px;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }
`;

const OrgBody = styled.div`
    padding: 16px;
    border-top: 1px solid ${color_shades_dark};
`;

const ConfigToggle = styled.button`
    background: transparent;
    border: 1px solid ${color_colors_ocean};
    color: ${color_colors_ocean};
    padding: 6px 14px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    margin-bottom: 12px;

    &:hover {
        background: ${color_colors_ocean};
        color: ${color_defaults_white};
    }
`;

const ConfigSection = styled.div`
    margin-bottom: 16px;
`;

const ExportImportBar = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
`;

const ExportImportButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        opacity: 0.9;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px;
    gap: 12px;
`;

const LoadingText = styled.span`
    font-size: 14px;
    color: ${color_text_light};
`;

const EmptyState = styled.div`
    padding: 24px;
    text-align: center;
    color: ${color_text_light};
    font-style: italic;
`;

const OverrideEditor = styled.div`
    margin-top: 16px;
    border: 2px solid ${color_colors_ocean};
    border-radius: 6px;
    overflow: hidden;
`;

const OverrideHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: rgba(30, 126, 185, 0.08);
    border-bottom: 1px solid ${color_colors_ocean};
`;

const OverrideTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${color_colors_ocean};
`;

const ForceIncludeSection = styled.div`
    margin-top: 20px;
    padding: 16px;
    background: ${color_shades_lightest};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
`;

const ForceIncludeTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_default};
    margin-bottom: 12px;
`;

const DropdownContainer = styled.div`
    position: relative;
    margin-bottom: 12px;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 13px;
    
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
`;

const DropdownList = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
`;

const DropdownItem = styled.div`
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid ${color_shades_lightest};
    
    &:hover {
        background: ${color_shades_lightest};
    }
    
    &:last-child {
        border-bottom: none;
    }
`;

const AppName = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_default};
    margin-bottom: 2px;
`;

const AppId = styled.div`
    font-size: 11px;
    color: ${color_text_light};
    font-family: monospace;
`;

const DropdownHint = styled.div`
    padding: 8px 12px;
    font-size: 12px;
    color: ${color_text_light};
    font-style: italic;
    text-align: center;
`;

const ForceIncludeList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ForceIncludeItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
`;

const ForceIncludeAppName = styled.div`
    font-size: 13px;
    color: ${color_text_default};
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ForceIncludeAppId = styled.span`
    font-size: 11px;
    color: ${color_text_light};
    font-family: monospace;
`;

const RemoveButton = styled.button`
    padding: 4px 10px;
    background: transparent;
    color: ${color_colors_decline};
    border: 1px solid ${color_colors_decline};
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;
    
    &:hover {
        background: ${color_colors_decline};
        color: ${color_defaults_white};
    }
`;

const IgnoredSection = styled.div`
    margin-top: 20px;
    padding: 16px;
    background: rgba(255, 0, 0, 0.03);
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
`;

const IgnoredTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_default};
    margin-bottom: 12px;
`;

const IgnoredList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const IgnoredItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    opacity: 0.7;
`;

const IgnoredAppName = styled.div`
    font-size: 13px;
    color: ${color_text_default};
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-decoration: line-through;
`;

const IgnoredAppId = styled.span`
    font-size: 11px;
    color: ${color_text_light};
    font-family: monospace;
`;

const EmptyIgnoredText = styled.div`
    font-size: 12px;
    color: ${color_text_light};
    font-style: italic;
`;


const OverrideActions = styled.div`
    display: flex;
    gap: 8px;
`;

const RemoveOverrideButton = styled.button`
    background: ${color_colors_decline};
    color: ${color_defaults_white};
    border: none;
    padding: 4px 10px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        opacity: 0.9;
    }
`;

const ExportButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 4px 10px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        opacity: 0.9;
    }
`;

const ImportButton = styled.button`
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    border: none;
    padding: 4px 10px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        opacity: 0.9;
    }
`;

const CloseButton = styled.button`
    background: ${color_shades_darker};
    color: ${color_defaults_white};
    border: none;
    padding: 4px 10px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        opacity: 0.9;
    }
`;

export default React.memo(OrganizationSection, (prevProps, nextProps) => {
    // Custom comparator: skip re-render when the data this component uses hasn't changed.
    // revenueContainer is a class instance — same reference always — so we compare
    // the specific state slices this component reads.
    if (prevProps.isExpanded !== nextProps.isExpanded) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.orgRevenue !== nextProps.orgRevenue) return false;
    if (prevProps.orgConfig !== nextProps.orgConfig) return false;
    if (prevProps.clipboardConfig !== nextProps.clipboardConfig) return false;
    // Check the state slices this component reads from the container
    const prevOrgId = prevProps.orgConfig.organizationId;
    const nextOrgId = nextProps.orgConfig.organizationId;
    const prevContainer = prevProps.revenueContainer.state;
    const nextContainer = nextProps.revenueContainer.state;
    if (prevContainer.applicationsByOrg[prevOrgId] !== nextContainer.applicationsByOrg[nextOrgId]) return false;
    if (prevContainer.engagementsByOrg[prevOrgId] !== nextContainer.engagementsByOrg[nextOrgId]) return false;
    return true;
});
